import React, { useEffect, useState } from "react";
import { api, euro } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CheckCircle2, Loader2, Sparkles, XCircle } from "lucide-react";

const digits = (v) => String(v || "").replace(/\D/g, "");
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

export const useUnoCheck = (on, number, iccid = "", piano = "", refresh = 0) => {
  const [res, setRes] = useState(null);
  useEffect(() => {
    if (!on || (digits(number).length < 9 && digits(iccid).length < 13)) { setRes(null); return undefined; }
    setRes((r) => (refresh && r?.found ? r : { loading: true }));
    const t = setTimeout(() => {
      api.get("/italiamobile/unomobile/lookup", { params: { number, iccid, piano, fresh: refresh > 0 } }).then(({ data }) => setRes(data)).catch(() => setRes({ on: true, found: null, error: "controllo non riuscito" }));
    }, refresh ? 0 : 700);
    return () => clearTimeout(t);
  }, [on, number, iccid, piano, refresh]);
  return res;
};

export const UnoNumberCheck = ({ res, testid = "uno-number-check" }) => {
  if (!res || res.on === false) return null;
  const row = "text-xs flex items-start gap-1.5";
  if (res.loading) return <p className={`${row} text-muted-foreground`} data-testid={testid}><Loader2 className="w-3.5 h-3.5 mt-px animate-spin" /> Controllo il numero sul portale Uno Mobile…</p>;
  if (res.found === false) return <p className={`${row} text-destructive`} data-testid={testid}><XCircle className="w-3.5 h-3.5 mt-px" /> Il numero non è di questo operatore.</p>;
  if (res.found === null) return <p className={`${row} text-amber-600`} data-testid={testid}><AlertTriangle className="w-3.5 h-3.5 mt-px" /> Non riesco a controllare il numero: {res.error}</p>;
  return <div className="space-y-1" data-testid={testid}>
    <p className={`${row} text-teal-600`} data-testid={`${testid}-ok`}><CheckCircle2 className="w-3.5 h-3.5 mt-px" /> SIM Uno Mobile · {res.summary}</p>
    {res.problem && <p className={`${row} text-destructive`} data-testid={`${testid}-problem`}><XCircle className="w-3.5 h-3.5 mt-px" /> {cap(res.problem)}</p>}
  </div>;
};

const STATE = { active: ["Attiva ora", "text-teal-600"], available: ["Si può scegliere", "text-primary"], blocked: ["Non si può scegliere", "text-muted-foreground"] };

export const UnoProfiles = ({ res }) => {
  const list = res?.found ? res.offers || [] : [];
  if (!list.length) return null;
  const free = list.filter((o) => o.state === "available").length;
  return <div className="rounded-lg border border-border p-3 space-y-1.5" data-testid="uno-profiles">
    <p className="text-sm font-medium">Profili per questa SIM</p>
    {list.map((o, i) => <div key={o.name} className="flex items-start justify-between gap-3 text-xs border-b border-border pb-1.5 last:border-0" data-testid={`uno-profile-${i}`}>
      <span className="min-w-0"><span className="text-sm text-foreground">{o.name}</span>{o.state === "blocked" && o.reason && <span className="block text-muted-foreground">{cap(o.reason)}</span>}</span>
      <span className={`shrink-0 font-medium ${STATE[o.state][1]}`} data-testid={`uno-profile-${i}-state`}>{STATE[o.state][0]}</span>
    </div>)}
    {!free && <p className="text-xs text-amber-600" data-testid="uno-profiles-none">Su questa SIM il portale non permette di passare a un altro profilo finché è attivo quello attuale.</p>}
  </div>;
};

export const UnoExtras = ({ res, busyId, onPick }) => {
  const list = res?.found ? res.extras || [] : [];
  if (!list.length) return null;
  const credit = res.credit ?? 0;
  const low = (x) => credit < x.cost;
  return <div className="rounded-lg border border-border p-3 space-y-2" data-testid="uno-extras">
    <p className="text-sm font-medium flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-primary" /> Extra con un tocco</p>
    <p className="text-xs text-muted-foreground" data-testid="uno-extras-credit">Li paga il cliente con il credito della SIM (ora {euro(credit)}): al tuo Plafond non scaliamo niente. Si attivano subito sul portale.</p>
    <div className="flex flex-wrap gap-2">
      {list.map((x) => <Button key={x.id} type="button" size="sm" variant="outline" className="gap-1.5" disabled={!!busyId || low(x)} onClick={() => onPick(x)} title={low(x) ? "Credito della SIM insufficiente" : ""} data-testid={`uno-extra-${x.id}`}>
        {busyId === x.id && <Loader2 className="w-3.5 h-3.5 animate-spin" />}{x.name} · {euro(x.cost)}
      </Button>)}
    </div>
    {list.some(low) && <p className="text-xs text-amber-600" data-testid="uno-extras-low">Quelli in grigio costano più del credito della SIM: fai prima una ricarica.</p>}
  </div>;
};
